import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { generalErrorHandler, isObject, request, validateFormInput } from '../../../utils'
import Analytics from '../../../utils/analytics'
import { getTranslate } from 'react-localize-redux'
import { connect } from 'react-redux'
import { getSchools } from '../../../reducers/SchoolsReducer'
import { deleteInstitutionOld, updateInstitution } from '../../../utils/api/institution'
import { SchoolContext } from '../../../Providers/SchoolProvider'
import { InstitutionDocumentsDrawerContext } from '../../../Providers/Drawer/InstitutionDocumentsDrawer'
import { HttpMethods } from '../../../utils/apiHelper'
import { isEncoder } from '../../../utils/roles'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import LinkingInstitutionModal from './LinkingInstitutionsModal'
import InstitutionSectors from '../../../Components/school/InstitutionSectors'
import SmartTable, { SmartTableDataTypes } from '../../../Components/shared/SmartTable'
import SmartTableCustomExportButton from '../../../Components/shared/SmartTableCustomExportButton'
import { getUser } from '../../../reducers/UserReducer'

const mapStateToProps = state => ({
  dataReady: state.getDataReady,
  schools: getSchools(state.getSchools),
  t: getTranslate(state.locale),
  user: getUser(state.getUser)
})

const InstitutionsView = ({ dataReady, schools, user, t }) => {
  const { countries, languages } = useContext(GlobalContext)
  const { institutions, setInstitutions, schoolLoading } = useContext(SchoolContext)
  const DocumentsContext = useContext(InstitutionDocumentsDrawerContext)

  const [visible, setVisible] = useState(false)
  const [selectedInstitutionSectors, setSelectedInstitutionSectors] = useState(null)

  const flattedLanguages = useMemo(() => {
    return languages.flatMap(l => l.languageLocales)
  }, [languages])

  const mappedInstitutions = useMemo(() => institutions.map(i => ({
    id: i.id,
    acronym: i.acronym,
    name: i.name,
    address: i.address,
    zipcode: i.zipcode,
    country: i.country === null ? null : i.country.id,
    city: i.city,
    description: i.description,
    countryDisabled: i.managed,
    managed: i.managed,
    filesCount: i.filesCount,
    email: i.email,
    languageLocale: i.languageLocale?.languageTag ?? null
  })), [institutions])

  const handleDocumentSelection = useCallback(institution => {
    if (DocumentsContext) {
      DocumentsContext.setSelectedInstitution({ data: institution })
    }
  }, [DocumentsContext])

  useEffect(() => {
    Analytics.pageView('/school-institution')
  }, [])

  const hasWritePermission = useMemo(() => {
    let canCrud = user.school?.premium ?? false

    if (canCrud) {
      canCrud = false

      if (schools.length > 0) {
        const { lockedInstitutionsCount } = schools[0]

        canCrud = lockedInstitutionsCount && isEncoder(user)
      } else if (isEncoder(user)) {
        canCrud = true
      }
    }

    return canCrud
  }, [user.school, user.roles, schools])

  const columns = useMemo(() => [
    { type: SmartTableDataTypes.ID, key: 'id' },
    {
      type: SmartTableDataTypes.STRING,
      name: t('Name'),
      key: 'name',
      validate: data => validateFormInput('freeText', data, true)
    },
    {
      type: SmartTableDataTypes.STRING,
      name: t('Email'),
      key: 'email',
      validate: data => validateFormInput('email', data, true)
    },
    {
      name: t('Acronym'),
      key: 'acronym',
      validate: data => validateFormInput('freeText', data, true)
    },
    {
      type: SmartTableDataTypes.STRING,
      name: t('Address'),
      key: 'address',
      validate: data => validateFormInput('freeText', data, true)
    },
    {
      type: SmartTableDataTypes.STRING,
      name: t('Zipcode'),
      key: 'zipcode',
      validate: data => validateFormInput('freeText', data, true)
    },
    {
      type: SmartTableDataTypes.STRING,
      name: t('City'),
      key: 'city',
      validate: data => validateFormInput('name', data, true)
    },
    {
      type: SmartTableDataTypes.SELECT,
      name: t('Country'),
      key: 'country',
      options: countries.map(c => {
        return { id: c.id, name: t('country.' + c.code) }
      }),
      disabled: !hasWritePermission

    },
    {
      type: SmartTableDataTypes.SELECT,
      name: t('Language'),
      key: 'languageLocale',
      options: flattedLanguages.map(ll => {
        return { id: ll.languageTag, name: ll.nameInNativeLanguage }
      }),
      disabled: !hasWritePermission
    },
    {
      type: SmartTableDataTypes.STRING,
      name: t('Description'),
      key: 'description'
    }
  ], [t, hasWritePermission, countries, languages])

  const addInstitution = (institution, stopLoading) => {
    institution.acronym = institution.name
    request('/api/Institution', HttpMethods.POST, institution, user)
      .then(json => {
        if (json.status !== 'error') {
          const languageObj = flattedLanguages.find(ll => ll.languageTag === institution.languageLocale)
          json.data.languageLocale = languageObj ?? null
          const countryObj = countries.find(c => c.id === institution.country)
          json.data.country = countryObj ?? null

          setInstitutions([...institutions, json.data])
        }

        stopLoading()
      })
      .catch(error => { generalErrorHandler(error) })
  }

  const editInstitution = institution => {
    const updatedInstitution = { ...institution }

    if (typeof updatedInstitution.countryDisabled !== 'undefined') {
      delete updatedInstitution.countryDisabled
    }

    if (typeof updatedInstitution.filesCount !== 'undefined') {
      delete updatedInstitution.filesCount
    }

    updateInstitution(user, updatedInstitution).then(json => {
      if (json?.data) {
        setInstitutions(institutions.map(i => {
          if (i.id === institution.id) {
            Object.keys(json.data).map(k => {
              if (k === 'languageLocale') {
                i[k] = json.data[k]
              } else {
                i[k] = isObject(i[k]) ? i[k] : json.data[k]
              }

              return k
            })
          }
          return i
        }))
      }
    })
  }

  const handleDeleteInstitution = institution => {
    deleteInstitutionOld(user, institution).then(json => {
      if (json?.data) {
        setInstitutions(institutions.filter(i => i.id !== institution.id))
      }
    })
  }

  const checkIfRowIsControllable = row => {
    if (row.id === -1) {
      return true
    }

    return isEncoder(user) && row.managed === false
  }

  return (
    <div>
      <SmartTable
        columns={columns}
        data={mappedInstitutions}
        loading={schoolLoading}
        onDataAdd={hasWritePermission ? addInstitution : undefined}
        secondaryButtons={[<IconButton key='link-institution' icon={faPlus} onClick={() => setVisible(!visible)} text={t('institutions_view.link_institution')} type='primary' />]}
        onDataEdit={hasWritePermission ? editInstitution : undefined}
        onDataDelete={hasWritePermission ? handleDeleteInstitution : undefined}
        noDataText={<span> {t("Votre établissement n'a encore aucune institution vers laquelle envoyer des étudiants en stage.")} </span>}
        addDataText={t('Add an institution')}
        customExportButton={<SmartTableCustomExportButton path='institutions' />}
        checkIfRowIsControllable={hasWritePermission ? checkIfRowIsControllable : undefined}
        additionalActions={[
          {
            iconName: 'file',
            type: 'primary',
            title: t('Manage files'),
            onClick: handleDocumentSelection,
            customClassCallback: data => data.filesCount > 0 ? 'green-button' : ''
          },
          {
            iconName: 'stethoscope',
            type: 'primary',
            title: t('See care units'),
            onClick: setSelectedInstitutionSectors
          }
        ]}
      />
      <InstitutionSectors
        institution={selectedInstitutionSectors}
        onClose={() => setSelectedInstitutionSectors(null)}
      />
      <LinkingInstitutionModal visible={visible} onCancel={() => setVisible(false)} />
    </div>
  )
}

export default connect(mapStateToProps)(InstitutionsView)
